import React, { useRef, useState } from 'react';
import { TestRunService } from '@/api/testRun';
import { IModuleSchema } from '@/api/module';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { ExportResult } from '@/components/result/ExportResult';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { ExtendedTableLowCode, ExtendedTableLowCodeRef } from '@/components/shared/table/ExtendedTableLowCode';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { CustomCellProperties } from 'alurkerja-ui';
import { IoShareSocialOutline } from 'react-icons/io5';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAccess } from '@/utils/hooks/useAccess';
import AddIssue from '@/components/testCase/AddIssue';
import { IDetailTestRunOptions, IDetailTestRunSchema, TestRunDetailService } from '@/api/testRunDetail';
import { MdBugReport } from 'react-icons/md';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { IProjectSchema, ProjectService } from '@/api/project';
import { TestCaseModule } from '@/components/module/TestCaseModule';
import { SelectFieldRow } from '@/components/shared/tableRow/SelectFieldRow';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { DeleteTestRunDetail } from '@/components/testRunDetail/DeleteTestRunDetail';
import { toastAlert } from '@/utils/libs/toastAlert';
import { ProjectAssigneeService } from '@/api/projectAssignee';
import 'react-toastify/dist/ReactToastify.css';
import { LinkTestIssue } from '@/components/testRunDetail/LinkTestIssue';
import { useProtectedRoute } from '@/routes/ProtectedRouteContext';
import DetailTestRunModal from '@/components/testRunDetail/ModalDetail';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { useIsExternal } from '@/utils/hooks/useUserType';

interface CustomCellProps extends CustomCellProperties {
  options?: IDetailTestRunOptions;
  assigneeOptions?: { label: string; value: string }[];
  isExternal?: boolean;
}

interface CustomActionCellProps {
  data: IDetailTestRunSchema;
  projectData: IProjectSchema;
  isViewer?: boolean;
  isExternal?: boolean;
  tableRef: React.RefObject<ExtendedTableLowCodeRef>;
  isPublic?: boolean;
}

const PublicCustomCell: React.FC<{
  name: string;
  rowValue: {
    [x: string]: any;
  };
}> = ({
  name,
  rowValue,
}) => {
  if (name === 'case_code') {
    return (
      <>
        {rowValue.test_case?.case_code}
        {rowValue.test_run_detail_issue?.[0] && (
          <MdBugReport className="text-red-500" />
        )}
      </>
    );
  } else if (name === 'type') {
    return <>{rowValue.test_case?.type}</>;
  } else if (name === 'title') {
    return (
      <div className="max-w-max flex items-center gap-2">
        <span>{rowValue.test_case?.title}</span>
        {(rowValue.test_case as any)?.deleted_at && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            Dihapus
          </span>
        )}
      </div>
    );
  } else if (name === 'assignee_id') {
    return <>{rowValue.assignee ? rowValue.assignee.full_name : '-'}</>;
  } else if (name === 'status') {
    return <div className="max-w-max">{rowValue.status}</div>;
  }
};

const CustomCell: React.FC<CustomCellProps> = ({
  name,
  defaultCell,
  rowValue,
  options,
  assigneeOptions,
  isExternal,
}) => {
  const { mutate: update } = TestRunDetailService.useUpdate();
  const { mutate: updateStatus } = TestRunDetailService.useUpdateStatus();
  const { isPublic } = useProtectedRoute();

  if (isPublic) {
    return <PublicCustomCell name={name} rowValue={rowValue} />;
  } else if (name === 'case_code') {
    return (
      <>
        {rowValue.test_case?.case_code}
        {rowValue.test_run_detail_issue?.[0] && (
          <MdBugReport className="text-red-500" />
        )}
      </>
    );
  } else if (name === 'type') {
    return <>{rowValue.test_case?.type}</>;
  } else if (name === 'title') {
    return (
      <div className="max-w-max flex items-center gap-2">
        <span>{rowValue.test_case?.title}</span>
        {(rowValue.test_case as any)?.deleted_at && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            Dihapus
          </span>
        )}
      </div>
    );
  } else if (name === 'assignee_id') {
    return (
      <SelectFieldRow
        key={rowValue.id}
        options={assigneeOptions ?? []}
        value={rowValue.assignee_id}
        disabled={isPublic || isExternal}
        onChange={value => {
          const toast = toastAlert();

          toast.info({ title: 'Waiting to update Assigned' });

          update(
            { ...rowValue, assignee_id: value },
            {
              onSuccess: () => {
                toast.close();
                toast.success({ title: 'Success update Assigned' });
              },
            },
          );
        }}
      />
    );
  } else if (name === 'status') {
    return (
      <SelectFieldRow
        key={rowValue.id}
        value={rowValue.status}
        disabled={isPublic}
        onChange={value => {
          const toast = toastAlert();

          toast.info({ title: 'Waiting to update Status' });

          if (isExternal) {
            updateStatus(
              {
                id: rowValue.id!,
                status: value,
              },
              {
                onSuccess: () => {
                  toast.close();
                  toast.success({ title: 'Success update Status' });
                },
                onError: () => {
                  toast.close();
                  toast.error({ title: 'Failed to update Status' });
                },
              },
            );
          } else {
            update(
              { ...rowValue, status: value },
              {
                onSuccess: () => {
                  toast.close();
                  toast.success({ title: 'Success update Status' });
                },
              },
            );
          }
        }}
        options={options?.status ?? []}
      />
    );
  } else if (name === 'updated_date_status') {
    return (
      <div className="max-w-max">
        {rowValue.updated_status_at
          ? (() => {
            const date = new Date(rowValue.updated_status_at);
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('id-ID', { month: 'long' });
            const year = date.getFullYear();
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${day} ${month} ${year} ${hours}:${minutes}`;
          })()
          : '-'}
      </div>
    );
  }

  return defaultCell;
};

const CustomActionCell: React.FC<CustomActionCellProps> = ({
  data,
  isViewer,
  isExternal,
  projectData,
  tableRef,
  isPublic,
}) => {
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  return (
    <>
      <DetailTestRunModal
        tesRunDetailId={data.id!}
        onSuccess={tableRef?.current?.refetch}
      />

      {!isViewer && !isPublic && !isExternal && (
        <>
          <AddIssue testRunDetail={data} onSuccess={ref?.current?.refetch} />

          {projectData?.id && (
            <LinkTestIssue
              testRunDetailId={data.id!}
              projectId={projectData.id}
              onSuccess={tableRef.current?.refetch}
              selectedTestIssue={data?.test_run_detail_issue?.map(
                issue => issue.test_issue_id,
              )}
            />
          )}

          <DeleteTestRunDetail
            testRunDetail={data}
            onSuccess={tableRef.current?.refetch}
          />
        </>
      )}
    </>
  );
};

interface DetailTestRunProps {
  projectId?: string;
}

export const DetailTestRun: React.FC<DetailTestRunProps> = ({ projectId }) => {
  const { isPublic } = useProtectedRoute();
  const location = useLocation();
  const { id, testRunId } = useParams();
  const resolvedProjectId = id ?? projectId;
  const navigate = useNavigate();
  const backNavigate = useBackNavigate();
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  // Service
  const { data: optionsData } = TestRunDetailService.useGetOptions();
  const { data: testRunData, isFetching: isTestRunDataFetching } =
    TestRunService.useGet(testRunId);
  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(resolvedProjectId);
  const { data: assigneeOptionsData } =
    ProjectAssigneeService.useGetOptions(resolvedProjectId);

  const isViewer = useAccess(resolvedProjectId!);
  const isExternal = useIsExternal();

  const [selectedModuleId, setSelectedModuleId] = useState<string | undefined>(
    undefined,
  );
  const [checkedModules] = useState<IModuleSchema[]>([]);

  const specDetailTestRun = useCustomSpec(`/api/crud/test-run-detail`, {
    columns: [
      { key: 'case_code', label: 'Case Code' },
      { key: 'type', label: 'Type' },
      { key: 'title', label: 'Title' },
      { key: 'updated_date_status', label: 'Updated Date Status' },
      { key: 'status', label: 'Status' },
      { key: 'assignee_id', label: 'Assigned To' },
    ],
  });

  const filterSpec: IFilterSpec[] = [
    {
      name: 'case_code',
      label: 'Case Code',
      type: 'text',
    },
    { name: 'title', label: 'Title', type: 'text' },
    {
      name: 'type',
      label: 'Type',
      type: 'multiOption',
      options: optionsData?.type,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'multiOption',
      options: optionsData?.status,
    },
    {
      name: 'assignee_id',
      label: 'Assigned To',
      type: 'multiOption',
      options: assigneeOptionsData?.assign,
    },
    { name: 'updated_date_status', label: 'Updated Date Status', type: 'daterange' },
  ];

  const toShare = () => navigate(`/public/shareable/test-run/${testRunId}`);

  const toModuleShare = () =>
    navigate(
      `/public/shareable/test-run/${testRunId}?moduleId=${selectedModuleId}`,
    );

  const shareModule = location.search.includes('moduleId');

  if (isTestRunDataFetching || isProjectFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <div className="tab-group">
      <Section
        title={`Test Run - ${testRunData?.title}`}
        actions={
          !isViewer && !isPublic ? (
            <div className="flex flex-row gap-3">
              {!isPublic && (
                <>
                  <ExportResult
                    id={testRunId}
                    projectName={testRunData?.title}
                  />
                  <OutlineButton
                    icon={<IoShareSocialOutline />}
                    onClick={toShare}
                  >
                    Share
                  </OutlineButton>
                  <OutlineButton
                    icon={<IoShareSocialOutline />}
                    onClick={toModuleShare}
                  >
                    Share Module
                  </OutlineButton>
                </>
              )}
            </div>
          ) : (
            <></>
          )
        }
      >
        <div className="flex flex-row min-h-screen">
          <div className="w-[20%]">
            <TestCaseModule
              isViewer
              showCheckedOnly
              checkedModules={checkedModules}
              project={projectData!}
              testRun={testRunData}
              onSelectedModule={module => {
                setSelectedModuleId(module.id);
              }}
              selectedShareModule={!!shareModule}
            />
          </div>

          <div className="w-[80%]">
            <ExtendedTableLowCode
              ref={ref}
              spec={specDetailTestRun}
              defaultFilter={{
                module_id: selectedModuleId || undefined,
                test_run_id: testRunId,
                is_archive: 'false',
              }}
              defaultSort="case_code"
              defaultSortOrder="asc"
              customButtonCreate={HideCustomCreateButton}
              customActionCell={data => (
                <CustomActionCell
                  data={data as IDetailTestRunSchema}
                  projectData={projectData as IProjectSchema}
                  isViewer={isViewer}
                  isExternal={isExternal}
                  tableRef={ref}
                  isPublic={isPublic}
                />
              )}
              customCell={props => (
                <CustomCell
                  {...props}
                  options={optionsData}
                  assigneeOptions={assigneeOptionsData?.assign}
                  isExternal={isExternal}
                />
              )}
              filterSpec={filterSpec}
              showFilter
            />
          </div>
        </div>
      </Section>

      {!isPublic && (
        <div className="tab-list">
          <OutlineButton onClick={backNavigate}>Back</OutlineButton>
        </div>
      )}
    </div>
  );
};
